import { motion } from "framer-motion";
import { Gift, Heart, Megaphone, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { HexIcon } from "@/components/HexIcon";

const ways = [
  {
    icon: Gift,
    title: "Give",
    body: "Donate diapers, formula, household essentials, or a financial gift — every single item goes directly to a Madison County family.",
    cta: { label: "Call to arrange a donation", href: "tel:4794375939", testid: "give-call-btn" },
    testid: "help-card-give",
  },
  {
    icon: Heart,
    title: "Volunteer",
    body: "Mentor a neighbor, sort donations, or lend a hand at community events. An hour of your time can change a family's whole week.",
    cta: { label: "Email us to volunteer", href: "mailto:Themadcohive@gmail.com", testid: "volunteer-email-btn" },
    testid: "help-card-volunteer",
  },
  {
    icon: Megaphone,
    title: "Spread the Word",
    body: "Follow The Hive on Facebook and share our posts — the next person who needs us may be on your friends list right now.",
    cta: { label: "Follow & share our page", href: "https://www.facebook.com/share/1CE1uxViXo/", testid: "share-facebook-btn", external: true },
    testid: "help-card-share",
  },
];

export default function WaysToHelp() {
  return (
    <section id="ways-to-help" data-testid="ways-to-help-section" className="honeycomb-light relative bg-cream py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-gold-deep" data-testid="help-eyebrow">
            Chapter 05 — Pitch In
          </p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="help-heading">
            A hive only works <span className="italic text-gold-deep">when we all do.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-ink-soft" data-testid="help-subtext">
            Every gift — money, supplies, time, or a simple share — stays right here in Madison County and lands in the hands of a neighbor who needs it.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {ways.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.1}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="group soft-shadow flex h-full flex-col rounded-[1.75rem] border border-transparent bg-white p-7 transition-colors duration-500 hover:border-gold/50"
                data-testid={w.testid}
              >
                <HexIcon size={64}>
                  <w.icon size={26} strokeWidth={1.8} />
                </HexIcon>
                <h3 className="mt-6 font-display text-xl font-semibold text-ink">{w.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-soft">{w.body}</p>
                <a
                  href={w.cta.href}
                  {...(w.cta.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid={w.cta.testid}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-gold-deep"
                >
                  {w.cta.label}
                  <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.25}>
          <p className="mt-12 text-center font-script text-3xl text-gold-deep" data-testid="help-script-quote">
            — Be the hands + feet of Jesus —
          </p>
        </Reveal>
      </div>
    </section>
  );
}
